import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertCircle, X, Loader2 } from 'lucide-react';
import { useQuotes } from '../hooks/useQuotes';
import { useAuth } from '../lib/AuthContext';
import ImageUpload from '../components/ImageUpload';
import type { CloudinaryResponse, QuoteCardData } from '../types';

const CARD_STYLES = [
  {
    id: 'blog',
    name: 'Blog',
    preview: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/blog.png?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'frame',
    name: 'Picture Frame',
    preview: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/pictureframe.png?auto=format&fit=crop&w=300&q=80'
  },
  {
    id: 'polaroid',
    name: 'Polaroid',
    preview: 'https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/polaroid.png?auto=format&fit=crop&w=300&q=80'
  }
];

export default function SendQuoteCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { quotes } = useQuotes();
  const { user } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  
  const [selectedStyle, setSelectedStyle] = useState(CARD_STYLES[0].id);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);
  const [uploadedImage, setUploadedImage] = useState<{ cloudinary: CloudinaryResponse; quote: QuoteCardData } | null>(null);

  const quote = quotes.find(q => q.id === id);

  if (!quote) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Quote not found</div>
      </div>
    );
  }

  const handleUploadComplete = (response: { cloudinary: CloudinaryResponse; quote: QuoteCardData }) => {
    setUploadedImage(response);
    setError(null);
    setDebugInfo(`Image uploaded successfully: ${response.cloudinary.url}`);
  };

  const handleUploadError = (errorMessage: string) => {
    setError(errorMessage);
    setDebugInfo(`Upload error: ${errorMessage}`);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setSuccess(false);
    setDebugInfo(null);

    try {
      const userEmail = user?.email || '';
      const usernamePrefix = userEmail.split('@')[0];

      const payload = {
        quoteId: quote.id,
        text: quote.text,
        author: quote.author,
        cardStyle: selectedStyle,
        userEmail,
        usernamePrefix,
        image: uploadedImage?.cloudinary
      };

      setDebugInfo(`Preparing to send data...\n${JSON.stringify(payload, null, 2)}`);

      const response = await fetch('https://hook.us1.make.com/4do7aaesuxu2qn5co8l6wa7qul8ohvqb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      setDebugInfo(prev => `${prev}\nResponse status: ${response.status}`);

      // Check if the response has content
      const contentType = response.headers.get('content-type');
      let responseData;

      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        // If not JSON, get the text response
        const textResponse = await response.text();
        responseData = { message: textResponse };
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseData.message || response.statusText}`);
      }

      setDebugInfo(prev => `${prev}\nResponse received:\n${JSON.stringify(responseData, null, 2)}`);

      setSuccess(true);
      if (formRef.current) {
        formRef.current.reset();
      }
      
      setTimeout(() => {
        navigate('/quotes');
      }, 2000);
    } catch (err) {
      console.error('Submit error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send quote card. Please try again.';
      setError(errorMessage);
      setDebugInfo(prev => `${prev}\nError: ${errorMessage}`);
    } finally {
      setSending(false);
    }
  };

  const quoteData: QuoteCardData = {
    quoteId: quote.id,
    text: quote.text,
    author: quote.author,
    cardStyle: selectedStyle,
    userEmail: user?.email || '',
    usernamePrefix: (user?.email || '').split('@')[0]
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Quotes</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Send Quote Card</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md flex items-center gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-700 hover:text-red-900"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-md flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              <span>Quote card sent successfully! Redirecting...</span>
            </div>
          )}

          {debugInfo && (
            <div className="mb-6 p-4 bg-gray-50 text-gray-700 rounded-md">
              <pre className="whitespace-pre-wrap text-sm font-mono">{debugInfo}</pre>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-2">Selected Quote</h2>
            <div className="p-4 bg-gray-50 rounded-md">
              <p className="text-lg text-gray-800 italic">"{quote.text}"</p>
              <p className="mt-2 text-gray-600">— {quote.author}</p>
            </div>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose a Card Style
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {CARD_STYLES.map((style) => (
                  <div
                    key={style.id}
                    onClick={() => setSelectedStyle(style.id)}
                    className={`relative rounded-lg cursor-pointer ${
                      selectedStyle === style.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                  >
                    <div className="aspect-[4/3] rounded-lg overflow-hidden">
                      <img
                        src={style.preview}
                        alt={style.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="absolute top-2 right-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        selectedStyle === style.id 
                          ? 'bg-blue-500' 
                          : 'bg-white bg-opacity-75'
                      }`}>
                        <CheckCircle2 className={`h-5 w-5 ${
                          selectedStyle === style.id 
                            ? 'text-white' 
                            : 'text-gray-400'
                        }`} />
                      </div>
                    </div>
                    <p className="mt-2 text-sm font-medium text-center text-gray-900">
                      {style.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Image (Optional)
              </label>
              <ImageUpload
                quoteData={quoteData}
                onUploadComplete={handleUploadComplete}
                onUploadError={handleUploadError}
              />
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  'Send Quote Card'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}